import type { ComponentProps, MouseEvent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '../cn';
import Button from '../Input2/Button';
import { General, Media } from '../StrokeIcons';

// Format seconds to mm:ss
function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// Pick a file extension for the downloaded recording: prefer the one on the URL path (Twilio/Telnyx
// recordings are archived as .mp3/.wav), otherwise fall back to the blob's MIME type, else default
// to mp3.
function audioFileExtension(src: string, mimeType?: string): string {
  const path = src.split('?')[0] ?? src;
  const pathMatch = path.match(/\.([a-z0-9]{2,4})$/i);
  if (pathMatch?.[1]) return pathMatch[1].toLowerCase();
  if (mimeType?.includes('wav')) return 'wav';
  if (mimeType?.includes('ogg')) return 'ogg';
  if (mimeType?.includes('webm')) return 'webm';
  if (mimeType?.includes('mp4') || mimeType?.includes('m4a') || mimeType?.includes('aac')) return 'm4a';
  return 'mp3';
}

export type MusicPlayerProps = ComponentProps<'div'> & {
  src?: string;
  /**
   * When provided, renders a download button that saves the audio as `${downloadFileName}.<ext>`.
   * The extension is derived from the source URL / blob type. Omit to hide the button.
   */
  downloadFileName?: string;
  /**
   * Seek request from outside (e.g. clicking a transcript line). A new object — even with the same
   * `seconds` — seeks again and starts playback. Omit for a self-contained player.
   */
  seekRequest?: { seconds: number } | null;
  /** Reports the playback position (seconds) as it plays, e.g. to highlight the current line. */
  onTimeChange?: (seconds: number) => void;
  /**
   * Page-level keys while mounted: Space plays / pauses, ← / → skip 5 s. Ignored while typing in a
   * field, with a button focused (Space already presses it) or inside a menu / listbox. For one focused player per page —
   * e.g. a call's detail drawer — never for a list of players.
   */
  keyboardShortcuts?: boolean;
};

/** How far ← / → skip with `keyboardShortcuts`. */
export const MUSIC_PLAYER_SKIP_SECONDS = 5;

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    tag === 'BUTTON' ||
    target.isContentEditable ||
    // A focused menu / dropdown owns its own arrow keys and Space.
    target.closest('[role="listbox"],[role="menu"],[role="combobox"],[role="option"]') != null
  );
}

export function MusicPlayer({
  src,
  className,
  downloadFileName,
  seekRequest,
  onTimeChange,
  keyboardShortcuts = false,
}: MusicPlayerProps) {
  const ref = useRef<HTMLAudioElement>(null);
  const [percentage, setPercentage] = useState(0);
  const [isPaused, setIsPaused] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  // Latest callback without re-binding the media listeners on every render.
  const onTimeChangeRef = useRef(onTimeChange);
  onTimeChangeRef.current = onTimeChange;

  // External seek (transcript click): move the playhead and play from there.
  useEffect(() => {
    const audio = ref.current;
    if (audio == null || seekRequest == null) return;
    audio.currentTime = Math.max(0, seekRequest.seconds);
    void audio.play().catch(() => {});
  }, [seekRequest]);

  useEffect(() => {
    const audio = ref.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      onTimeChangeRef.current?.(audio.currentTime);
      if (audio.duration) {
        setPercentage((audio.currentTime / audio.duration) * 100);
      }
    };

    const handleEnded = () => {
      audio.currentTime = 0;
      setIsPaused(true);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handlePlay = () => setIsPaused(false);
    const handlePause = () => setIsPaused(true);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, []);

  useEffect(() => {
    if (!keyboardShortcuts) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const audio = ref.current;
      if (audio == null || e.metaKey || e.ctrlKey || e.altKey || isTypingTarget(e.target)) return;
      if (e.key === ' ') {
        e.preventDefault();
        if (audio.paused) void audio.play().catch(() => {});
        else audio.pause();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const delta = e.key === 'ArrowLeft' ? -MUSIC_PLAYER_SKIP_SECONDS : MUSIC_PLAYER_SKIP_SECONDS;
        const end = Number.isFinite(audio.duration) ? audio.duration : Number.POSITIVE_INFINITY;
        audio.currentTime = Math.min(end, Math.max(0, audio.currentTime + delta));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [keyboardShortcuts]);

  const onToggle = async (e?: MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();

    if (ref.current?.paused) {
      await ref.current?.play();
    } else {
      ref.current?.pause();
    }
  };

  const onSeek = async (seekPercentage: number) => {
    if (isPaused) {
      await onToggle();
    }
    // A stream with unknown length reports Infinity (or NaN before metadata): nothing to seek within.
    if (ref.current != null && Number.isFinite(ref.current.duration) && ref.current.duration > 0) {
      ref.current.currentTime = (seekPercentage * ref.current.duration) / 100;
    }
  };

  const onDownload = async (e?: MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (!src || isDownloading) return;
    setIsDownloading(true);
    try {
      // Fetch the bytes so the browser honors our custom filename. A plain <a download> is
      // ignored for cross-origin (GCS) URLs, which would save the opaque object name instead.
      const response = await fetch(src);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${downloadFileName}.${audioFileExtension(src, blob.type)}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      // CORS / network failure: fall back to opening the raw file so the user can still save it.
      window.open(src, '_blank', 'noopener,noreferrer');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={className}>
      <audio src={src} ref={ref} />
      <div className="flex w-full flex-row items-center">
        <Bar
          percentage={percentage}
          currentTime={currentTime}
          duration={duration}
          onSeek={onSeek}
          playPauseContent={
            <button
              onClick={onToggle}
              aria-label={isPaused ? 'Play' : 'Pause'}
              // z-10 keeps the button above the (relative, later-in-DOM) time-label row so its
              // transparent left padding can't steal the click and turn a pause into a seek.
              className="absolute left-4 top-1/2 z-10 flex h-6 w-auto -translate-y-1/2 cursor-pointer text-white/75 transition-colors hover:text-white">
              {isPaused && <Media.Play className="h-full w-auto" />}
              {!isPaused && <Media.PauseCircle className="h-full w-auto" />}
            </button>
          }
          // Rendered on the right edge of the (always-dark) bar so the white icon keeps its
          // contrast regardless of the surrounding card color.
          downloadContent={
            downloadFileName != null && src != null ? (
              <Button
                text
                size="sm"
                aria-label="Download recording"
                icon={<General.Download01 />}
                tooltip="Download recording"
                status={isDownloading ? 'updating' : undefined}
                onClick={onDownload}
                className="pointer-events-auto shrink-0 text-white/75 hover:text-white"
              />
            ) : undefined
          }
        />
      </div>
    </div>
  );
}

type BarProps = {
  percentage: number;
  currentTime?: number;
  duration?: number;
  onSeek?: (percentage: number) => void;
  playPauseContent?: ReactNode;
  downloadContent?: ReactNode;
};

function Bar({ percentage, currentTime, duration, onSeek, playPauseContent, downloadContent }: BarProps) {
  const onBarClicked = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const elWidth = e.currentTarget.clientWidth;
    const clickedAtWidth = e.clientX - e.currentTarget.getBoundingClientRect().left;
    const clickPercentage = (clickedAtWidth / elWidth) * 100;
    onSeek?.(clickPercentage);
  };

  return (
    <div
      className="relative flex h-12 w-full min-w-[120px] cursor-pointer items-center overflow-hidden rounded-lg bg-slate-600"
      onClick={onBarClicked}>
      <div
        className="absolute inset-y-0 left-0 bg-[#0B2058] transition-[width] duration-100"
        style={{ width: `${percentage}%` }}
      />
      {playPauseContent}
      {/* Time labels are non-interactive; pointer-events-none so this overlay never intercepts
          clicks meant for the play/pause button (left) or the seek bar (elsewhere). The download
          button (right) opts back in via pointer-events-auto so it stays clickable. */}
      <div className="pointer-events-none relative flex w-full items-center gap-2 pl-12 pr-3 text-sm font-medium text-white">
        {currentTime != null && <span>{formatTime(currentTime)}</span>}
        {duration != null && !isNaN(duration) && <span className="ml-auto">{formatTime(duration)}</span>}
        {/* Negative vertical margin keeps a taller icon button from stretching the label row. */}
        {downloadContent != null && (
          <span className={cn('-my-2 shrink-0', (duration == null || isNaN(duration)) && 'ml-auto')}>
            {downloadContent}
          </span>
        )}
      </div>
    </div>
  );
}
